import React, { useState, useEffect } from "react";
import { Container, Button, Table, Modal, Form, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import Sidebar from "./Sidebar";

const FiEditTyped = FiEdit as unknown as React.FC<{ size?: number }>;
const FiTrash2Typed = FiTrash2 as unknown as React.FC<{ size?: number }>;

interface Brand {
  id: number;
  name: string;
  autologin_url: string;
}

const API_BASE = "https://api.performancedashboard.io/api/metricool";

const AccessUrlPage: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [name, setName] = useState("");
  const [autologin, setAutologin] = useState("");

  const fetchBrands = async () => {
    try {
      setError(null);
      const res = await fetch(`${API_BASE}/brands`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch brands: ${res.status}`);
      }
      const data: Brand[] = await res.json();
      setBrands(data);
    } catch (err: any) {
      setError(err.message);
      console.error("Error fetching brands:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    fetchBrands();
  }, [token, navigate]);

  const handleAdd = () => {
    setEditingBrand(null);
    setName("");
    setAutologin("");
    setShowModal(true);
    setError(null);
  };

  const handleEdit = (brand: Brand) => {
    setEditingBrand(brand);
    setName(brand.name);
    setAutologin(brand.autologin_url);
    setShowModal(true);
    setError(null);
  };

  const handleSave = async () => {
    if (!name || !autologin) {
      setError("Both brand name and access URL are required");
      return;
    }

    try {
      new URL(autologin);
    } catch {
      setError("Invalid URL format");
      return;
    }

    // Metricool URL validation
    const metricoolPattern = /^https:\/\/app\.metricool\.com\/autoin\/[A-Z0-9]+$/i;
    if (!metricoolPattern.test(autologin)) {
      setError("Please enter a valid Metricool URL (e.g., https://app.metricool.com/autoin/...)");
      return;
    }

    try {
      setError(null);
      const payload = { name, autologin_url: autologin };
      const res = await fetch(
        editingBrand ? `${API_BASE}/brands/${editingBrand.id}` : `${API_BASE}/brands`,
        {
          method: editingBrand ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "include",
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to save brand: ${res.status}`);
      }

      setShowModal(false);
      await fetchBrands();
    } catch (err: any) {
      setError(err.message);
      console.error("Error saving brand:", err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this Access URL?")) return;

    try {
      setError(null);
      const res = await fetch(`${API_BASE}/brands/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete brand: ${res.status}`);
      }

      await fetchBrands();
    } catch (err: any) {
      setError(err.message);
      console.error("Error deleting brand:", err);
    }
  };

  return (
    <Container fluid style={{ height: "100vh", display: "flex", padding: 0 }}>
      <Sidebar
        onLogout={() => {
          localStorage.removeItem("authToken");
          navigate("/");
        }}
      />

      <div style={{ flex: 1, padding: "20px", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
          <h2>Manage URLs</h2>
          <Button variant="primary" onClick={handleAdd}>
            Add Access URL
          </Button>
        </div>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>#</th>
              <th>Brand Name</th>
              <th>Access URL</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {brands.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center">
                  No Access URLs found
                </td>
              </tr>
            ) : (
              brands.map((b) => (
                <tr key={b.id}>
                  <td>{b.id}</td>
                  <td>{b.name}</td>
                  <td style={{ wordBreak: "break-all" }}>{b.autologin_url}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEdit(b)}
                      style={{ marginRight: "5px" }}
                    >
                      <FiEditTyped size={16} />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(b.id)}
                    >
                      <FiTrash2Typed size={16} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{editingBrand ? "Edit Access URL" : "Add Access URL"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && (
              <Alert variant="danger" onClose={() => setError(null)} dismissible>
                {error}
              </Alert>
            )}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Brand Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter brand name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Access URL</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="Enter Metricool access URL"
                  value={autologin}
                  onChange={(e) => setAutologin(e.target.value)}
                  required
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={loading}>
              {editingBrand ? "Save Changes" : "Add URL"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
};

export default AccessUrlPage;
